import {DI_TOKENS} from '@/di/di.tokens';
import {IItemData} from '@/stores/ItemsStore/ItemsStore.types';
import {uuid4} from '@sentry/utils';

import {injected} from 'brandi';

import {HttpError, ProjectUserAuth} from '@specials/project-user';

import {COMPLETION_MODAL} from '@/constants';
import {ResourcesAll} from '@/resources';
import {CacheService} from '@/services/CacheService/CacheService';
import {DevToolsMbService} from '@/services/DevToolsMbService/DevToolsMbService';
import {ErrorService} from '@/services/ErrorService/ErrorService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {ProjectApiHeaders} from '@/services/ProjectApi/ProjectApiHeaders';
import {IProjectApi} from '@/services/ProjectApi/types';
import {ProjectUserInfoData, ProjectUserLoginData} from '@/services/ProjectAuthApi/types';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {AuthStore} from '@/stores/AuthStore/AuthStore';
import {BonusesStore} from '@/stores/BonusesStore/BonusesStore';
import {ItemsStore} from '@/stores/ItemsStore/ItemsStore';
import {UserItemsStore} from '@/stores/UserItemsStore/UserItemsStore';
import {UserEnergyStore} from '@/stores/UserStores/UserEnergyStore';
import {UserStore} from '@/stores/UserStores/UserStore';
import {Config, GlobalWindow} from '@/types';
import {ItemContentSharedUseCase} from '@/useCases/shared/ItemContentSharedUseCase/ItemContentSharedUseCase';
import {ModalControlSharedUseCase} from '@/useCases/shared/ModalControlSharedUseCase/ModalControlSharedUseCase';
import {TimerUpdateSharedUseCase} from '@/useCases/shared/TimerUpdateSharedUseCase/TimerUpdateSharedUseCase';

export class InitializeUseCase {
  constructor(
    private readonly projectApi: IProjectApi,
    private readonly projectUserAuth: ProjectUserAuth<ProjectUserInfoData, ProjectUserLoginData>,
    private readonly trackingService: TrackingService,
    private readonly loggerService: LoggerService,
    private readonly userStore: UserStore,
    private readonly userEnergyStore: UserEnergyStore,
    private readonly authStore: AuthStore,
    private readonly platformService: PlatformService,
    private readonly projectApiHeaders: ProjectApiHeaders,
    private readonly globalWindow: GlobalWindow,
    private readonly timerUpdateSharedUseCase: TimerUpdateSharedUseCase,
    private readonly itemContentSharedUseCase: ItemContentSharedUseCase,
    private readonly modalControlSharedUseCase: ModalControlSharedUseCase,
    private readonly config: Config,
    private readonly bonusesStore: BonusesStore,
    private readonly itemsStore: ItemsStore,
    private readonly userItemsStore: UserItemsStore,
    private readonly cacheService: CacheService,
    private readonly devtoolsMbService: DevToolsMbService,
    private readonly errorService: ErrorService,
    private readonly resources: ResourcesAll,
  ) {}

  async init(): Promise<void> {
    this.projectApiHeaders.setSessionIdData(uuid4());

    this.platformService.init();

    void this.devtoolsMbService.initMBDevTools();

    if (this.platformService.isMb()) {
      // для мобильной версии отключаем избыточный bouncing scroll (в основном ios) для избежания неприятных артефактов
      // съезжания верстки
      // @see: https://jira.tcsbank.ru/browse/SP-18434
      this.globalWindow.document.documentElement.style.overscrollBehavior = 'none';
    }

    if (this.platformService.isMb()) {
      this.projectApiHeaders.setMbHeadersData();
    }

    this.userStore.subscribe((userState) => {
      this.loggerService.log('userState', userState);
    });

    this.authStore.subscribe((authState) => {
      this.loggerService.log('authState', authState);
    });

    this.projectApi.onUnauthorizedHttpErrorProject(() => {
      // если при запросе к api проекта получили HttpProjectError с кодом 401, разлогиним пользователя в модели
      this.authStore.setAuthStateGuest();
    });

    this.projectApi.onHttpErrorBackend((error) => {
      // Договорились логировать в sentry все ошибки API,
      // которые не соответствуют контракту HttpErrorProject
      // @See: https://wiki.tcsbank.ru/display/DIGEDEV/%5BPS%5D+Meetings+%7C+Frontend
      this.loggerService.logHttpError(error);
    });

    this.projectUserAuth.subscribe((projectUserAuthState) => {
      this.loggerService.setUserId(projectUserAuthState.data?.userInfo?.id);
      // логируем Cookie, чтобы в логе ошибки видеть, что Cookie у пользователя не пустые,
      // потому что в WebView есть кейс когда Cookie очищаются
      this.loggerService.setCookies(this.globalWindow.document.cookie);
      this.trackingService.updateClientParams(projectUserAuthState.data?.userInfo?.id);

      this.authStore.setAuthState(projectUserAuthState);

      if (projectUserAuthState.data?.userInfo) {
        const {energyValue, nextEnergyIn, items, bonuses, userItems, isOpenedAllItems, ...restUserData} =
          projectUserAuthState.data.userInfo;

        const {mappedStatisticItems} = this.itemContentSharedUseCase.getMappedStatisticResources();
        const itemsWithStatistic = items.filter(
          (item) => item.userStatistics.length || mappedStatisticItems[item.label]?.defaultStatistic,
        );

        const transformedItemsWithStatistic = itemsWithStatistic.reduce<Record<string, IItemData>>((acc, item) => {
          return {
            ...acc,
            [item.id]: item,
          };
        }, {});
        const userItemsWithStatistic = userItems.filter((userItem) =>
          Boolean(transformedItemsWithStatistic[userItem.itemId]),
        );

        this.userStore.setUserState(restUserData);

        this.userEnergyStore.setUserEnergy({
          nextIn: nextEnergyIn,
          value: energyValue,
          maxValue: itemsWithStatistic.length - userItemsWithStatistic.length,
        });
        const unwatchedBonusIds = this.cacheService.getUnwatchedBonuses();
        this.bonusesStore.setBonuses(bonuses, unwatchedBonusIds);

        // маппим контент статистики, чтобы установить его один раз
        const itemsWithStatisticContent = itemsWithStatistic.map((item) => {
          return {
            ...item,
            itemStatisticContent: this.itemContentSharedUseCase.getItemContent(item.userStatistics),
          };
        });

        this.itemsStore.setItems(itemsWithStatisticContent);
        this.userItemsStore.setUserItems(userItemsWithStatistic);

        const isUserItemsEqualToItems = itemsWithStatistic.length === userItemsWithStatistic.length;

        if ((isUserItemsEqualToItems && !isOpenedAllItems) || (!isUserItemsEqualToItems && isOpenedAllItems)) {
          void this.updateIsOpenedAllItems(isUserItemsEqualToItems);
        }

        const isAllItemsOpenedModalShown = this.cacheService.getIsAllItemsOpenedModal();
        const isAllItemsOpened = itemsWithStatisticContent.length === userItemsWithStatistic.length;

        if (!isAllItemsOpenedModalShown && isAllItemsOpened) {
          this.cacheService.saveAllItemsOpenedModal();
        }

        const isFinishesSoonModalShown = this.cacheService.getIsFinishesSoonModal();
        const isFinishedModalShown = this.cacheService.getIsFinishedModal();

        const currentTypeCompletionModal = this.resources.config.currentTypeCompletionModal as COMPLETION_MODAL;
        const isProjectFinishedModalActive = this.resources.config.isProjectFinishedModalActive;

        const isProjectFinished = currentTypeCompletionModal === COMPLETION_MODAL.ProjectFinished;
        const isProjectFinishesSoon = currentTypeCompletionModal === COMPLETION_MODAL.ProjectFinishesSoon;

        if (!isFinishesSoonModalShown && isProjectFinishesSoon) {
          this.modalControlSharedUseCase.handleOpenProjectFinishesSoon();
        }

        // добавляем условие isProjectFinishedModalActive,
        // чтобы модалки о завершении и закрытии проекта не накладывались друг на друга
        if (!isFinishedModalShown && isProjectFinished && !isProjectFinishedModalActive) {
          this.modalControlSharedUseCase.handleOpenProjectFinished();
        }
      }

      if (this.authStore.isLoggedIn()) {
        this.timerUpdateSharedUseCase.init();
        this.trackingService.trackAuthUserValid(projectUserAuthState.data?.userInfo?.items.map((item) => item.id));
        const userId = projectUserAuthState.data?.userInfo?.id;

        userId && this.projectApiHeaders.setUserIdData(userId);
      } else if (this.authStore.isError()) {
        const error = this.authStore.getErrorData();
        this.trackingService.trackAuthError(this.errorService.buildErrorData(error).breadcrumbs.errorCode);
      }

      if (!this.authStore.isLoading()) {
        this.trackingService.trackLoadPage();
      }
    });

    // После того как подписались на изменения состояния авторизации, запускаем саму авторизацию.
    // Подписываемся на событие subscribeTrackingPageLoad только если страница не встроена в iframe, потому что
    // инициализация трекинга в iframe приводит к задвоению событий аналитики, поэтому в iframe трекинг не инициализируется
    // @See: https://wiki.tcsbank.ru/pages/viewpage.action?pageId=1900144132
    this.trackingService.subscribeTrackingModule(() => void this.projectUserAuth.auth());
  }

  public loginOnAuthPageInDevelopmentMode = (): void => {
    if (this.config.isLocal) {
      void this.projectUserAuth.auth({forceLogin: true});
    }
  };

  private updateIsOpenedAllItems = async (isOpenedAllItems: boolean): Promise<void> => {
    try {
      await this.projectApi.updateIsOpenedAllItems({isOpenedAllItems});
    } catch (error: unknown) {
      this.loggerService.logHttpError(error as HttpError);
    }
  };
}

injected(
  InitializeUseCase,
  DI_TOKENS.projectApi,
  DI_TOKENS.projectUserAuth,
  DI_TOKENS.trackingService,
  DI_TOKENS.loggerService,
  DI_TOKENS.userStore,
  DI_TOKENS.userEnergyStore,
  DI_TOKENS.authStore,
  DI_TOKENS.platformService,
  DI_TOKENS.projectApiHeaders,
  DI_TOKENS.globalWindow,
  DI_TOKENS.timerUpdateSharedUseCase,
  DI_TOKENS.itemContentSharedUseCase,
  DI_TOKENS.modalControlSharedUseCase,
  DI_TOKENS.config,
  DI_TOKENS.bonusesStore,
  DI_TOKENS.itemsStore,
  DI_TOKENS.userItemsStore,
  DI_TOKENS.cacheService,
  DI_TOKENS.devtoolsMbService,
  DI_TOKENS.errorService,
  DI_TOKENS.resources,
);
